'use client';

import type { TypeSelect } from 'widgets/project-creation-stepper/providers/service-provider';
import { useServiceType } from 'widgets/project-creation-stepper/providers/service-provider';
import type { IServiceSelect } from 'widgets/project-creation-stepper/model';
import { useCallback, useEffect, useMemo } from 'react';
import FormControlWithLabel from 'shared/ui/form-control-with-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select';
import type { StepperProps } from 'widgets/project-creation-stepper/ui/upload-component';
import { useQuery } from '@tanstack/react-query';
import { getMarketplaceConfigs } from '@/enteties/marketplace/api';
import { TextFieldSkeleton } from 'shared/ui/form-elements';
import * as m from '@paraglide/messages';
const dbTypes: TypeSelect[] = [{
  name: 'cnpg',
  label: 'postgresql'
}
// { name: 'pxc', label: 'mysql' },
];
export const ServiceComponent = ({
  next
}: StepperProps) => {
  const serviceTypes: IServiceSelect[] = useMemo(() => [{
    name: 'app',
    label: m.create_dialog_step_service_serviceTypes_app()
  }, {
    name: 'db',
    label: m.create_dialog_step_service_serviceTypes_db()
  }, {
    name: 'marketplace',
    label: m.create_dialog_step_service_serviceTypes_marketplace()
  }], []);
  const marketplaceLabels = useMemo(() => ({
    mq: m.create_dialog_step_service_marketPlaceLables_mq(),
    db: m.create_dialog_step_service_marketPlaceLables_db(),
    authorization: m.create_dialog_step_service_marketPlaceLables_authorization(),
    bi: m.create_dialog_step_service_marketPlaceLables_bi(),
    db_utils: m.create_dialog_step_service_marketPlaceLables_db_utils(),
    'low-code': 'Low-code',
    mail_and_notifications: m.create_dialog_step_service_marketPlaceLables_db_mail_and_notifications(),
    ai: m.create_dialog_step_service_marketPlaceLables_ai()
  }), []);
  const {
    serviceType,
    setServiceType,
    selectedProjectType,
    setSelectedProjectType,
    projectTypes,
    setProjectTypes,
    firstLoad,
    setFirstLoad,
    selectedMarketplace,
    setSelectedMarketplace
  } = useServiceType();
  const {
    data: marketplaceConfig,
    isPending,
    isSuccess
  } = useQuery({
    ...getMarketplaceConfigs.getMarketplaceConfigsQueryOptions()
  });
  const marketplaceTypes = useMemo(() => {
    if (marketplaceConfig) {
      const mkKeys = Object.keys(marketplaceConfig);
      return mkKeys.map(key => ({
        name: key,
        label: marketplaceLabels[key as keyof typeof marketplaceLabels]
      }));
    }
  }, [marketplaceConfig]);
  const appTypes = useMemo(() => {
    if (marketplaceConfig && selectedProjectType) {
      return marketplaceConfig[selectedProjectType];
    }
  }, [marketplaceConfig, selectedProjectType]);
  const handleChange = useCallback((value: IServiceSelect['name']) => {
    switch (value) {
      case 'db':
        setProjectTypes(dbTypes);
        break;
      case 'marketplace':
        setProjectTypes(marketplaceTypes!);
        break;
      default:
        break;
    }
    setServiceType(value);
    setSelectedProjectType(undefined);
    setSelectedMarketplace(undefined);
  }, [marketplaceTypes, setProjectTypes, setSelectedMarketplace, setSelectedProjectType, setServiceType]);
  useEffect(() => {
    if (isSuccess && serviceType === 'marketplace') {
      handleChange(serviceType);
    }
  }, [isSuccess]);
  const handleProjectSelect = useCallback((value: string) => {
    setSelectedProjectType(value);
    setSelectedMarketplace(undefined);
  }, [setSelectedMarketplace, setSelectedProjectType]);
  const projectSelect = useMemo(() => {
    if (serviceType === 'marketplace' && isPending) return <TextFieldSkeleton variant="dark" />;
    if (!projectTypes || projectTypes.length === 0) return null;
    if ((!projectTypes || projectTypes.length === 0) && !selectedProjectType) return null;
    if (serviceType === 'app') return null;
    return <FormControlWithLabel text={m.create_dialog_step_service_projectType()} htmlFor="projectType">
        <Select onValueChange={handleProjectSelect} value={selectedProjectType || ''}>
          <SelectTrigger>
            <SelectValue placeholder={m.create_dialog_step_service_projectType_placeholder()} />
          </SelectTrigger>
          <SelectContent className="overflow-y-auto max-h-64">
            {projectTypes.map(projType => <SelectItem key={projType.name} value={projType.name}>
                {projType.label}
              </SelectItem>)}
          </SelectContent>
        </Select>
      </FormControlWithLabel>;
  }, [handleProjectSelect, isPending, projectTypes, selectedProjectType, serviceType]);
  const handleMarketplaceSelect = useCallback((value: string) => {
    setSelectedMarketplace(value);
  }, [setSelectedMarketplace]);
  const appSelect = useMemo(() => {
    if (!appTypes || appTypes.length === 0) return null;
    return <FormControlWithLabel text={m.create_dialog_step_service_marketType()}>
        <Select onValueChange={setSelectedMarketplace} value={selectedMarketplace || ''}>
          <SelectTrigger>
            <SelectValue placeholder={m.create_dialog_step_service_marketType_placeholder()} />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(appTypes).map(appType => <SelectItem key={appType} value={appType}>
                {appType}
              </SelectItem>)}
          </SelectContent>
        </Select>
      </FormControlWithLabel>;
  }, [appTypes, selectedMarketplace, setSelectedMarketplace]);
  useEffect(() => {
    if (serviceType && firstLoad) {
      handleChange(serviceType);
      if (serviceType === 'app') {
        next();
        setFirstLoad(false);
      }
      if (selectedProjectType) {
        handleProjectSelect(selectedProjectType);
        next();
        if (selectedMarketplace) {
          handleMarketplaceSelect(selectedMarketplace);
        }
        setFirstLoad(false);
      }
    }
  }, []);
  return <div className="grid gap-4 pb-2" data-sentry-component="ServiceComponent" data-sentry-source-file="service-component.tsx">
      <div className="grid gap-2">
        <FormControlWithLabel text={m.create_dialog_step_service_serviceType()} htmlFor="serviceType" data-sentry-element="FormControlWithLabel" data-sentry-source-file="service-component.tsx">
          <Select onValueChange={handleChange} defaultValue={serviceType} data-sentry-element="Select" data-sentry-source-file="service-component.tsx">
            <SelectTrigger data-sentry-element="SelectTrigger" data-sentry-source-file="service-component.tsx">
              <SelectValue placeholder={m.create_dialog_step_service_serviceType_placeholder()} data-sentry-element="SelectValue" data-sentry-source-file="service-component.tsx" />
            </SelectTrigger>
            <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="service-component.tsx">
              {serviceTypes.map(({
              name,
              label
            }) => <SelectItem key={name} value={name}>
                  {label}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </FormControlWithLabel>
      </div>
      <div className="grid gap-2">
        {projectSelect}
        {appSelect}
      </div>
    </div>;
};